/**
 * Hairline spec strip: equal mono cells (tiny uppercase label over a mono
 * figure). Used by both chat and generation cards.
 */
export function StatStrip({ children }: { children: React.ReactNode }) {
  return (
    <dl className="grid auto-cols-fr grid-flow-col divide-x divide-[var(--color-border)] rounded-xl border border-[var(--color-border)]">
      {children}
    </dl>
  )
}

export function StatCell({
  label,
  value,
  unit,
}: {
  label: string
  value: string
  unit?: string
}) {
  return (
    <div className="min-w-0 px-3 py-2">
      <dt className="truncate font-mono text-[0.625rem] uppercase tracking-[0.14em] text-[var(--color-text-tertiary)]">
        {label}
      </dt>
      <dd className="mt-1 truncate font-mono text-body-sm tabular-nums text-[var(--color-text-primary)]">
        {value}
        {unit && (
          <span className="ml-1 text-[var(--color-text-tertiary)]">{unit}</span>
        )}
      </dd>
    </div>
  )
}

/** Headline rate: caption label over a large tabular sans figure. */
export function PriceFigure({
  label,
  value,
  unit,
}: {
  label: string
  value: string
  unit?: string
}) {
  return (
    <div className="min-w-0">
      <dt className="text-caption text-[var(--color-text-tertiary)]">{label}</dt>
      <dd className="mt-1 truncate text-2xl font-semibold tracking-[-0.03em] tabular-nums text-[var(--color-text-primary)]">
        {value}
        {unit && (
          <span className="ml-0.5 text-body-sm font-normal tracking-normal text-[var(--color-text-tertiary)]">
            {unit}
          </span>
        )}
      </dd>
    </div>
  )
}

/** Secondary rate shown inline: `label $value`. */
export function PriceCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-1 text-caption">
      <dt className="text-[var(--color-text-tertiary)]">{label}</dt>
      <dd className="font-mono tabular-nums text-[var(--color-text-secondary)]">
        {value}
      </dd>
    </div>
  )
}

/** Tiny mono hairline tag (capabilities, generation params). */
export function TagChip({
  icon: Icon,
  children,
}: {
  icon?: React.ComponentType<{ className?: string }>
  children: React.ReactNode
}) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border)] px-1.5 py-0.5 font-mono text-[0.6875rem] leading-4 text-[var(--color-text-secondary)]">
      {Icon && <Icon className="h-3 w-3 text-[var(--color-text-tertiary)]" />}
      {children}
    </span>
  )
}
